import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, PiggyBank, Search } from 'lucide-react'
import { fetchBanked } from '../api/attendance'
// The same name filter the measuring queue uses (A-17): one answer to "does
// this name contain what she typed", rather than a second copy of four lines.
import { matching } from '../api/measuring'

/**
 * Who is holding banked classes (D-745).
 *
 * `asked`: *"Is there currently a way for teachers to see what kids have banked
 * for attendance? I believe we show it on the parents flow but not the
 * teachers ... a new tab inside attendance for tonya and inside classes for
 * teachers ... rows of names with like a number available and a number that has
 * been applied or something, and you can click them to expand and see what types
 * of classes are bankable they have."*
 *
 * **One screen, two doors, scoped by the server** (D-83, D-188): a teacher
 * reaches it from her Classes and sees her own dancers; the studio reaches it
 * from Attendance and sees everybody. The same shape `/my-standing` already
 * takes, because the alternative is a teacher sent to the studio's address and
 * told *Not your page*.
 *
 * **Most going spare at the top.** The list is read to find somebody with credit
 * nobody has used; ordering by name buries that among the dancers whose credits
 * are already spent.
 */
export default function BankedClassesView() {
  const [query, setQuery] = useState('')

  const { data, isPending, isError } = useQuery({
    queryKey: ['banked'],
    queryFn: fetchBanked,
  })

  const held = data ?? []
  const rows = useMemo(() => matching(held, query), [held, query])

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Attendance</p>
        <h1 className="font-display text-2xl">Banked classes</h1>
        <p className="mt-1.5 max-w-[42rem] text-[13.5px] text-ink-3">
          A class a dancer took that she did not have to &mdash; it waits in her bank and covers
          the first absence it fits. Only dancers holding one are listed.
        </p>
      </header>

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}
      {isError && (
        <p role="alert" className="text-sm text-crit">
          Could not load the banked classes.
        </p>
      )}

      {/*
        <b>Typing, not scrolling</b> (D-745). The list is short today and the
        summer import adds a name for every dancer who banked one, so reaching a
        particular family has to be typing - the same reason the measuring queue
        has one, and the same filter.
      */}
      {held.length > 0 && (
        <label className="relative mb-4 flex items-center">
          <Search size={15} className="pointer-events-none absolute left-3 text-ink-3" />
          <span className="sr-only">Find a dancer</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name"
            className="w-full rounded-xl border border-line bg-surface py-2.5 pr-3 pl-9 text-[14px]"
          />
        </label>
      )}

      {/*
        <b>Three empties, and they are different facts</b> (D-189, D-209).
        Nobody has banked anything is about the studio; nobody matches what she
        typed is about the filter and has to name it, or it reads as the first;
        and a failed load is neither.
      */}
      {data && held.length === 0 && (
        <p className="rounded-xl border border-line bg-surface px-4 py-3 text-[13.5px] text-ink-3">
          Nobody has a banked class yet.
        </p>
      )}

      {held.length > 0 && rows.length === 0 && (
        <p className="text-[13.5px] text-ink-3">
          Nobody with a banked class matches &ldquo;{query.trim()}&rdquo;.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {rows.map((row) => (
          <details
            key={row.dancerId}
            className="group rounded-2xl border border-line bg-surface px-4 py-3"
          >
            <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 py-1">
              <span
                className="inline-block transition-transform group-open:rotate-90"
                aria-hidden="true"
              >
                &#9656;
              </span>

              <span className="min-w-0 flex-1 text-[14px] font-semibold wrap-anywhere">{row.name}</span>

              {row.available > 0 ? (
                <span className="tabular shrink-0 rounded-md bg-good-bg px-2 py-0.5 text-[12px] font-semibold text-good">
                  <PiggyBank size={11} strokeWidth={2.5} className="mr-1 inline" />
                  {row.available} available
                </span>
              ) : (
                <span className="tabular shrink-0 rounded-md bg-surface-2 px-2 py-0.5 text-[12px] font-semibold text-ink-3">
                  <Check size={11} strokeWidth={2.5} className="mr-1 inline" />
                  All used
                </span>
              )}

              {/*
                Counted rather than hidden: a studio asking who has credit needs
                to see that a dancer had three and has used two, or the list
                reads as though the bank does nothing.
              */}
              {row.applied > 0 && (
                <span className="tabular w-full text-[12.5px] text-ink-3">
                  {row.applied} already applied to an absence
                </span>
              )}
            </summary>

            <ul className="mt-2 flex flex-col gap-1 border-l-2 border-surface-3 pl-3">
              {row.kinds.map((kind) => (
                <li
                  key={kind.label}
                  className="flex flex-wrap items-baseline justify-between gap-2 text-[12.5px]"
                >
                  <span className="text-ink-2 wrap-anywhere">{kind.label}</span>
                  <span className="tabular text-ink-3">
                    {/* A kind with nothing left says only what has gone: "0
                        available" is a number nobody needs and it reads as a
                        fault rather than as a bank that did its job. */}
                    {kind.available > 0 && <>{kind.available} available</>}
                    {kind.available > 0 && kind.applied > 0 && <> &middot; </>}
                    {kind.applied > 0 && <>{kind.applied} applied</>}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
    </div>
  )
}
